import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaExclamationTriangle, 
  FaClock, 
  FaSpinner, 
  FaCheck, 
  FaPrint,
  FaFilePdf,
  FaEnvelope,
  FaEdit,
  FaEye,
  FaThumbsDown,
  FaThumbsUp,
  FaTags,
  FaFileDownload
} from 'react-icons/fa';
import { getQuotationById, updateQuotation } from '../../services/quotation/quotationService';
import quotationEmailService from '../../services/email/quotationEmailService';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import './quotation-detail-print.css';

const QuotationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Editable fields
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  
  // Fetch the quotation data
  useEffect(() => {
    console.log('QuotationDetail mounted, fetching data for ID:', id);
    fetchQuotation();

    // Debug render issues
    console.log('Initial state:', {
      loading,
      error,
      quotation,
      items,
      notes,
      editMode
    });
  }, [id]);
  
  // Debug helper function
  const logItems = () => {
    console.log('Current items:', items);
    console.log('Raw quotation object:', quotation);
    
    // Log detallado para depurar precios MOQ
    console.log('Precios detallados por producto:');
    items.forEach((item, index) => {
      console.log(`Producto ${index + 1}: ${item.name}`);
      console.log('  - precio base:', item.price);
      console.log('  - price200:', item.price200);
      console.log('  - price500:', item.price500);
      console.log('  - moqOptions:', item.moqOptions);
      console.log('  - Datos completos:', item);
    });
  };
  
  const fetchQuotation = async () => {
    try {
      setLoading(true);
      const data = await getQuotationById(id);
      
      if (data) {
        setQuotation(data);
        // Initialize editable fields with current values
        let quotationItems = data.get('items') || [];
        
        // Ensure we have all needed fields for each item
        quotationItems = quotationItems.map(item => ({
          ...item,
          quantity: parseInt(item.quantity) || 200,
          price: parseFloat(item.price) || 0,
          price200: parseFloat(item.price200 || (item.pricing?.price200) || 0),
          price500: parseFloat(item.price500 || (item.pricing?.price500) || 0),
          priceAdjustment: parseFloat(item.priceAdjustment) || 0,
          moq: item.moq || 200
        }));
        
        console.log('Items cargados:', quotationItems);
        setItems(quotationItems);
        setNotes(data.get('additionalNotes') || '');
      } else {
        setError('No se pudo obtener la cotización solicitada.');
      }
    } catch (err) {
      console.error('Error fetching quotation:', err);
      setError(`Error al cargar los datos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Update a specific item's property in the items array
  const handleItemChange = (index, field, value) => {
    setItems(prevItems => {
      const newItems = [...prevItems];
      
      // Update the specified field
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
      
      // If quantity is being changed, update price based on MOQ tiers
      if (field === 'quantity') {
        const item = newItems[index];
        
        // When quantity changes to 200 or 500, update price if we have specific pricing
        if (value === 200 && item.price200) {
          newItems[index].price = parseFloat(item.price200);
          console.log('Updated price to 200 MOQ price:', item.price200);
        } else if (value === 500 && item.price500) {
          newItems[index].price = parseFloat(item.price500);
          console.log('Updated price to 500 MOQ price:', item.price500);
        }
      }
      
      return newItems;
    });
  };
  
  // Save changes to the quotation
  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('Items a guardar:', items);
      
      // Prepare updated data for Parse
      const itemsToSave = items.map(item => ({
        ...item,
        // Ensure numeric fields are saved as numbers
        quantity: parseInt(item.quantity) || 0,
        price: parseFloat(item.price) || 0,
        priceAdjustment: parseFloat(item.priceAdjustment) || 0
      }));
      
      // Crear un objeto simple para actualizar
      const updatedData = {
        items: itemsToSave,
        additionalNotes: notes
      };
      
      await updateQuotation(id, updatedData);
      toast.success('Cotización actualizada correctamente');
      
      // Refresh data
      await fetchQuotation();
    } catch (err) {
      console.error('Error saving quotation:', err);
      toast.error(`Error al guardar: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };
  
  // Send email with updated quotation
  const handleSendEmail = async () => {
    try {
      setSending(true);
      await quotationEmailService.sendQuotationUpdate(id);
      toast.success('Email enviado correctamente');
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error(`Error al enviar email: ${err.message}`);
    } finally {
      setSending(false);
    }
  };
  
  // Generate PDF function
  const generatePDF = async () => {
    try {
      setGenerating(true);
      
      // Create new PDF document con mejor calidad y fuentes
      const doc = new jsPDF({
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: true
      });
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20; // márgenes consistentes
      
      // Añadir logo e información de VV Health
      try {
        // Intentar cargar el logo (ajustar la ruta según donde esté almacenado el logo)
        const logoUrl = '/images/VVH LOGO Transparent/Logo Final_Centralizado_Sin Slogan.png';
        const logoDataURL = new URL(logoUrl, window.location.origin).href;
        doc.addImage(logoDataURL, 'PNG', margin, 15, 20, 20); // Ajustado tamaño para mejor visualización
      } catch (err) {
        console.error('Error al cargar el logo:', err);
        // Si falla la carga del logo, usar texto como fallback
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0); // Negro
        doc.text('VV Health', margin, 20);
      }
      
      // Para la versión de admin no mostramos la información de VV Health
      
      // Añadir título de cotización a la derecha con mejor formato
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0); // Negro como solicitaste
      doc.text(`Cotización #${quotation.id.substring(0, 8)}`, pageWidth - margin, 25, { align: 'right' });
      
      // Add quotation info
    doc.setFontSize(11);
    
    // Columna izquierda con mejor formato y espaciado
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('Datos de Cliente:', margin, 60);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`ID: `, margin, 67);
    doc.text(`Cliente: `, margin, 74);
    doc.text(`Email: `, margin, 81);
    doc.text(`Teléfono: `, margin, 88);
    
    // Valores de los datos del cliente
    doc.setFont('helvetica', 'bold');
    let idOffset = doc.getTextWidth('ID: ');
    let clienteOffset = doc.getTextWidth('Cliente: ');
    let emailOffset = doc.getTextWidth('Email: ');
    let telefonoOffset = doc.getTextWidth('Teléfono: ');
    
    doc.text(`${quotation.id}`, margin + idOffset, 67);
    doc.text(`${quotation.get('userName') || 'No especificado'}`, margin + clienteOffset, 74);
    doc.text(`${quotation.get('userEmail') || 'No especificado'}`, margin + emailOffset, 81);
    doc.text(`${quotation.get('userPhone') || 'No especificado'}`, margin + telefonoOffset, 88);
    
    // Columna derecha con el mismo estilo
    doc.setFont('helvetica', 'normal');
    doc.text(`Fecha: `, pageWidth - margin - 50, 67);
    doc.text(`Estado: `, pageWidth - margin - 50, 74);
    
    // Valores de fecha y estado
    doc.setFont('helvetica', 'bold');
    let fechaOffset = doc.getTextWidth('Fecha: ');
    let estadoOffset = doc.getTextWidth('Estado: ');
    
    doc.text(`${formatDate(quotation.createdAt)}`, pageWidth - margin - 50 + fechaOffset, 67);
    doc.text(`${getStatusLabel(quotation.get('status') || 'pending')}`, pageWidth - margin - 50 + estadoOffset, 74);
    
    // No incluir el magic link en el PDF - es demasiado largo y no relevante para el documento
      
      // Add items table
    const tableColumn = ['Producto', 'Pastillas/Frasco', 'Cantidad', 'Precio'];
    const tableRows = [];
    
    // Add data rows
    items.forEach(item => {
      // Obtener pastillas por frasco
      let pillCount = 'N/A';
      if (item.countPerBottle && Array.isArray(item.countPerBottle) && item.countPerBottle.length > 0) {
        // Eliminar 'ct' si está presente
        pillCount = item.countPerBottle[0].replace(/ct$/i, '');
      }
      
      const priceData = calculateItemPrice(item);
      tableRows.push([
        item.name,
        pillCount,
        item.quantity ? item.quantity.toString() : '0',
        priceData.totalPrice > 0 ? `$${priceData.totalPrice.toFixed(2)}` : 'A cotizar'
      ]);
    });
      
      // Agregar una línea separadora antes de la tabla de productos
      doc.setDrawColor(200, 200, 200); // Gris claro
      doc.setLineWidth(0.5);
      doc.line(margin, 95, pageWidth - margin, 95);
      
      // Título de la sección de productos
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Productos Solicitados', margin, 102);
      
      // Add table con mejor formato y espaciado
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 105, // Ajustado después del título de sección
        margin: { left: margin, right: margin },
        theme: 'grid',
        styles: {
          font: 'helvetica',
          fontSize: 10,
          cellPadding: 5,
          lineColor: [220, 220, 220], // Gris claro para líneas
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [248, 204, 61], // Amarillo VV Health
          textColor: [0, 0, 0], // Negro
          fontStyle: 'bold',
          halign: 'left'
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250] // Fondo claro alternado
        },
        columnStyles: {
          0: { fontStyle: 'bold' } // Nombre del producto en negrita
        }
      });
      
      // Add total con mejor formato - con verificación de seguridad para lastAutoTable
      let finalY = 105; // Valor por defecto si no hay tabla
      if (doc.lastAutoTable && doc.lastAutoTable.finalY) {
        finalY = doc.lastAutoTable.finalY + 15; // Mejor espaciado después de la tabla
      }
      
      // Línea separadora antes de los totales
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(pageWidth/2, finalY - 5, pageWidth - margin, finalY - 5);
      
      // Si el total es 0, mostrar "A cotizar"
      const total = calculateTotal();
      const totalText = total > 0 ? `$${total.toFixed(2)}` : 'A cotizar';
      
      // Totales con mejor formato
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('Subtotal:', pageWidth - 60, finalY);
      doc.text('Total:', pageWidth - 60, finalY + 10);
      
      // Valores de los totales
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(totalText, pageWidth - margin, finalY, { align: 'right' });
      doc.text(totalText, pageWidth - margin, finalY + 10, { align: 'right' });
      
      // Sección de notas con mejor formato
      let notesY = finalY + 20;
      if (notes && notes.trim().length > 0) {
        // Línea separadora antes de las notas
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.line(margin, notesY - 5, pageWidth - margin, notesY - 5);
        
        // Título de notas
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0); // Negro como solicitaste
        doc.text('Notas', margin, notesY + 5);
        
        // Contenido de las notas
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0); // Negro
        const splitNotes = doc.splitTextToSize(notes, pageWidth - (margin * 2));
        doc.text(splitNotes, margin, notesY + 15);
      }
      
      // Pie de página mejorado
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Línea superior del footer
      doc.setDrawColor(248, 204, 61); // Amarillo VV Health para la línea
      doc.setLineWidth(0.75);
      doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
      
      // Datos del footer
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0); // Negro
      const footerText = 'VV Health - www.vvhealthpr.com - info@vvhealth.com';
      doc.text(footerText, pageWidth/2, pageHeight - 10, { align: 'center' });
      
      // Save PDF con nombre más descriptivo
      const formattedDate = new Date().toISOString().split('T')[0];
      doc.save(`VVHealth_Cotizacion_${quotation.id.substring(0, 8)}_${formattedDate}.pdf`);
      toast.success('PDF generado correctamente');
    } catch (err) {
      console.error('Error generando PDF:', err);
      toast.error(`Error al generar PDF: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };
  
  // Print function
  const handlePrint = () => {
    window.print();
  };
  
  // Toggle between view and edit modes
  const toggleEditMode = () => {
    setEditMode(!editMode);
  };
  
  if (loading) {
    return (
      <div className="container py-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">{error}</div>
        <button onClick={() => navigate('/admin/quotations')} className="btn btn-secondary">
          <FaArrowLeft className="me-2" /> Volver a Cotizaciones
        </button>
      </div>
    );
  }
  
  if (!quotation) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">No se encontró la cotización solicitada.</div>
        <button onClick={() => navigate('/admin/quotations')} className="btn btn-secondary">
          <FaArrowLeft className="me-2" /> Volver a Cotizaciones
        </button>
      </div>
    );
  }
  
  // Formato de fecha
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };
  
  // Función de cálculo de precio basada en MOQ
  const calculateItemPrice = (item) => {
    try {
      // Ensure we're working with numbers
      const quantity = parseInt(item.quantity) || 0;
      let basePrice = parseFloat(item.price) || 0;
      const priceAdjustment = parseFloat(item.priceAdjustment) || 0;
      
      // Check for MOQ-specific prices
      if (quantity === 200 && item.price200) {
        basePrice = parseFloat(item.price200);
      } else if (quantity === 500 && item.price500) {
        basePrice = parseFloat(item.price500);
      }
      
      // Make sure we have a valid quantity and don't fall below MOQ
      const moq = parseInt(item.moq) || 200;
      const validQuantity = Math.max(quantity, moq);
      
      // Calculate total
      const subtotal = validQuantity * basePrice;
      const totalPrice = subtotal + priceAdjustment;
      
      return {
        basePrice,
        quantity: validQuantity,
        priceAdjustment,
        subtotal,
        totalPrice
      };
    } catch (error) {
      console.error('Error calculating price for item:', item, error);
      return {
        basePrice: 0,
        quantity: 0,
        priceAdjustment: 0,
        subtotal: 0,
        totalPrice: 0
      };
    }
  };
  
  // Calculate total for all items
  const calculateTotal = () => {
    if (!items || !Array.isArray(items) || items.length === 0) return 0;
    
    try {
      return items.reduce((sum, item) => {
        if (!item) return sum;
        const priceData = calculateItemPrice(item);
        return sum + (priceData?.totalPrice || 0);
      }, 0);
    } catch (error) {
      console.error('Error calculating total:', error);
      return 0;
    }
  };
  
  // Format price with currency
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PR', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };
  
  // Helper functions for status
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'processing': return 'primary';
      case 'completed': return 'success';
      case 'rejected': return 'danger';
      default: return 'secondary';
    }
  };
  
  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'processing': return 'En Proceso';
      case 'completed': return 'Completada';
      case 'rejected': return 'Rechazada';
      default: return 'Desconocido';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaClock className="text-warning me-2" />;
      case 'processing':
        return <FaSpinner className="text-primary me-2" />;
      case 'completed':
        return <FaCheck className="text-success me-2" />;
      case 'rejected':
        return <FaExclamationTriangle className="text-danger me-2" />;
      default:
        return null;
    }
  };
  
  // Always render a basic structure, even if there are issues with loading or errors
  
  // Now we know we have data, render the full component
  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 no-print">
        <h1 className="h3">Detalle de Cotización</h1>
        <div className="d-flex">
          <button 
            className="btn btn-outline-primary me-2"
            onClick={toggleEditMode}
          >
            {editMode ? (
              <><FaEye className="me-2" /> Modo Visualización</>
            ) : (
              <><FaEdit className="me-2" /> Modo Edición</>
            )}
          </button>
          
          {editMode && (
            <button 
              className="btn btn-primary me-2"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <><FaSpinner className="fa-spin me-2" /> Guardando...</> : 'Guardar Cambios'}
            </button>
          )}
          
          <button 
            className="btn btn-success me-2"
            onClick={generatePDF}
            disabled={generating}
          >
            {generating ? (
              <><FaSpinner className="fa-spin me-2" /> Generando...</>
            ) : (
              <><FaFilePdf className="me-2" /> Descargar PDF</>
            )}
          </button>
          
          {/* El botón de imprimir ha sido eliminado por solicitud del usuario */}
          
          <button 
            className="btn btn-secondary me-2"
            onClick={handleSendEmail}
            disabled={sending}
          >
            {sending ? (
              <><FaSpinner className="fa-spin me-2" /> Enviando...</>
            ) : (
              <><FaEnvelope className="me-2" /> Enviar Actualización</>
            )}
          </button>
          
          <button 
            onClick={() => navigate('/admin/quotations')} 
            className="btn btn-outline-dark"
          >
            <FaArrowLeft className="me-2" /> Volver
          </button>
        </div>
      </div>
      
      <div className="card mb-4 quotation-info-card">
        <div className="card-header bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Información General</h5>
            <span className={`badge bg-${getStatusColor(quotation?.get('status') || 'pending')}`}>
              {getStatusIcon(quotation?.get('status'))}
              {getStatusLabel(quotation?.get('status') || 'pending')}
            </span>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <p className="mb-1"><strong>ID:</strong> {quotation?.id}</p>
              <p className="mb-1"><strong>Cliente:</strong> {quotation?.get('userName') || 'No especificado'}</p>
              <p className="mb-1"><strong>Email:</strong> {quotation?.get('userEmail') || 'No especificado'}</p>
              <p className="mb-1"><strong>Teléfono:</strong> {quotation?.get('userPhone') || 'No especificado'}</p>
              <p className="mb-1"><strong>Perfil Profesional:</strong> {quotation?.get('userProfile') || 'No especificado'}</p>
            </div>
            <div className="col-md-6">
              <p className="mb-1"><strong>Creada:</strong> {quotation?.createdAt ? formatDate(quotation.createdAt) : '-'}</p>
              <p className="mb-1"><strong>Última actualización:</strong> {quotation?.updatedAt ? formatDate(quotation.updatedAt) : '-'}</p>
              <p className="mb-1">
                <strong>Magic Link:</strong> 
                <code className="ms-1 small">{quotation?.get('magicLink')}</code>
              </p>
              <p className="mb-1"><strong>Tipo de Producto:</strong> {quotation?.get('productType') || 'No especificado'}</p>
              <p className="mb-1"><strong>Convertida a cuenta:</strong> {quotation?.get('converted') ? 'Sí' : 'No'}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">Productos Solicitados</h5>
        </div>
        <div className="card-body">
          {items && items.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Producto</th>
                    <th>MOQ</th>
                    {editMode ? (
                      <>
                        <th>Precio Unit.</th>
                        <th>Ajuste</th>
                      </>
                    ) : (
                      <th>Precio</th>
                    )}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => {
                    const priceData = calculateItemPrice(item);
                    
                    return (
                      <tr key={index}>
                        <td>
                          <div className="product-info">
                            <div className="product-name">{item.name}</div>
                            <div className="product-details">
                              {item.code && (
                                <small className="d-block text-muted">
                                  <strong>Código:</strong> {item.code}
                                </small>
                              )}
                              {item.countPerBottle && (
                                <small className="d-block text-muted">
                                  <strong>Pastillas por pote:</strong> {Array.isArray(item.countPerBottle) ? item.countPerBottle.join(', ') : item.countPerBottle}
                                </small>
                              )}
                              {item.bulkQuantity && (
                                <small className="d-block text-muted">
                                  <strong>Bulk Quantity:</strong> {item.bulkQuantity}
                                </small>
                              )}
                              {item.categoryName && (
                                <small className="d-block text-muted">
                                  <strong>Categoría:</strong> {item.categoryName}
                                </small>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          {editMode ? (
                            <select 
                              className="form-select form-select-sm"
                              value={item.quantity}
                              onChange={(e) => {
                                handleItemChange(index, 'quantity', parseInt(e.target.value) || 0);
                              }}
                            >
                              <option value="200">200 unidades</option>
                              <option value="500">500 unidades</option>
                              {item.moq && item.moq !== 200 && item.moq !== 500 && (
                                <option value={item.moq}>{item.moq} unidades</option>
                              )}
                            </select>
                          ) : (
                            <span>{item.quantity || 0} unidades</span>
                          )}
                          {item.moq && (
                            <div>
                              <small className="text-muted">Mínimo: {item.moq}</small>
                            </div>
                          )}
                        </td>
                        {editMode ? (
                          <>
                            <td>
                              <div className="input-group input-group-sm">
                                <span className="input-group-text">$</span>
                                <input 
                                  type="number" 
                                  className="form-control form-control-sm" 
                                  value={item.price || ''} 
                                  onChange={(e) => {
                                    handleItemChange(index, 'price', parseFloat(e.target.value) || 0);
                                  }}
                                  step="0.01"
                                  min="0"
                                />
                              </div>
                              {item.price200 && item.price500 && (
                                <div className="mt-1">
                                  <small className="text-muted d-block">200u: ${item.price200}</small>
                                  <small className="text-muted d-block">500u: ${item.price500}</small>
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="input-group input-group-sm">
                                <span className="input-group-text">$</span>
                                <input 
                                  type="number" 
                                  className="form-control form-control-sm" 
                                  value={item.priceAdjustment || ''} 
                                  onChange={(e) => {
                                    handleItemChange(index, 'priceAdjustment', parseFloat(e.target.value) || 0);
                                  }}
                                  step="0.01"
                                  min="0"
                                />
                              </div>
                            </td>
                          </>
                        ) : (
                          <td>
                            {formatPrice(item.price || 0)}
                            {item.priceAdjustment > 0 && (
                              <small className="d-block text-success">+{formatPrice(item.priceAdjustment)}</small>
                            )}
                          </td>
                        )}
                        <td>
                          {formatPrice(priceData.totalPrice)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={editMode ? "4" : "3"} className="text-end"><strong>Subtotal:</strong></td>
                    <td>
                      {formatPrice(calculateTotal())}
                    </td>
                  </tr>
                  <tr className="table-primary">
                    <td colSpan={editMode ? "4" : "3"} className="text-end"><strong>Total:</strong></td>
                    <td>
                      <strong>
                        {formatPrice(calculateTotal())}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="alert alert-info">No hay productos en esta cotización.</div>
          )}
        </div>
      </div>
      
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">Notas</h5>
        </div>
        <div className="card-body">
          <textarea 
            className="form-control"
            rows="4"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Añadir notas o comentarios..."
          ></textarea>
        </div>
      </div>
      
      {/* Action buttons at the bottom of the page */}
      <div className="d-flex flex-wrap justify-content-between gap-2 mb-4 mt-4">
        <div className="d-flex flex-wrap gap-2">
          <button
            className={`btn ${editMode ? 'btn-success' : 'btn-primary'}`}
            onClick={toggleEditMode}
          >
            <FaEdit className="me-2" /> {editMode ? 'Guardar Cambios' : 'Modo Edición'}
          </button>
          
          <button 
            className="btn btn-success" 
            onClick={generatePDF}
            disabled={loading}
          >
            <FaFileDownload className="me-2" /> Descargar PDF
          </button>
          
          {/* Botón de imprimir removido según solicitud */}
          
          <button 
            className="btn btn-secondary" 
            onClick={handleSendEmail}
            disabled={loading || sending}
          >
            <FaEnvelope className="me-2" /> {sending ? 'Enviando...' : 'Enviar Actualización'}
          </button>
        </div>
        
        <button 
          className="btn btn-outline-primary" 
          onClick={() => navigate('/admin/quotations')}
        >
          <FaArrowLeft className="me-2" /> Volver
        </button>
      </div>
    </div>
  );
};

export default QuotationDetail;
